import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./LessonPlanForm.module.css";
import FormField from "./FormField";
import { FileContext } from "./FileContext";
import { track } from "../utils/analytics";
import { useLanguage } from "../contexts/LanguageContext";
import { t } from "../utils/translations";

const MOKU_OPTIONS = [
  { value: "general", label: "All of Hawaiʻi (General)", moku: "General", island: "All" },
  { value: "Hawaiʻi-Hilo", label: "Hawaiʻi - Hilo", moku: "Hilo", island: "Hawaiʻi" },
  { value: "Hawaiʻi-Kona", label: "Hawaiʻi - Kona", moku: "Kona", island: "Hawaiʻi" },
  { value: "Hawaiʻi-Hāmākua", label: "Hawaiʻi - Hāmākua", moku: "Hāmākua", island: "Hawaiʻi" },
  { value: "Maui-Hāna", label: "Maui - Hāna", moku: "Hāna", island: "Maui" },
  { value: "Maui-Wailuku", label: "Maui - Wailuku", moku: "Wailuku", island: "Maui" },
  { value: "Oʻahu-Koʻolaupoko", label: "Oʻahu - Koʻolaupoko", moku: "Koʻolaupoko", island: "Oʻahu" },
  { value: "Oʻahu-ʻEwa", label: "Oʻahu - ʻEwa", moku: "ʻEwa", island: "Oʻahu" },
  { value: "Oʻahu-Kona", label: "Oʻahu - Kona", moku: "Kona", island: "Oʻahu" },
  { value: "Kauaʻi-Haleleʻa", label: "Kauaʻi - Haleleʻa", moku: "Haleleʻa", island: "Kauaʻi" },
  { value: "Kauaʻi-Puna", label: "Kauaʻi - Puna", moku: "Puna", island: "Kauaʻi" },
  { value: "Kauaʻi-Kona (Waimea)", label: "Kauaʻi - Kona (Waimea)", moku: "Kona (Waimea)", island: "Kauaʻi" },
  { value: "Molokaʻi-Koʻolau", label: "Molokaʻi - Koʻolau", moku: "Koʻolau", island: "Molokaʻi" },
];

function LessonPlanForm() {
  const { language: uiLanguage } = useLanguage();
  const location = useLocation();
  const state = location.state || {};
  const { resumeData, isResuming } = state;

  /* Prefill from location.state when returning from select-elements (same inputs = reuse cache) */
  const [standardsData, setStandardsData] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(state.selectedSubject ?? resumeData?.subject ?? "cs");
  const [selectedGrade, setSelectedGrade] = useState(state.selectedGrade ?? resumeData?.grade ?? "");
  const [selectedConcept, setSelectedConcept] = useState(state.selectedConcept ?? resumeData?.concept ?? "");
  const [selectedSubconcept, setSelectedSubconcept] = useState(state.selectedSubconcept ?? resumeData?.subconcept ?? "");
  const [selectedStandard, setSelectedStandard] = useState(() => {
    if (state.selectedStandardIdentifier != null && state.selectedStandardDescription != null) {
      return { identifier: state.selectedStandardIdentifier, standard: state.selectedStandardDescription };
    }
    if (resumeData?.standard != null && resumeData?.standard_description != null) {
      return { identifier: resumeData.standard, standard: resumeData.standard_description };
    }
    return resumeData?.standard ?? "";
  });
  const [selectedLanguage, setSelectedLanguage] = useState(state.selectedLanguage ?? resumeData?.language ?? "en");
  const [selectedMoku, setSelectedMoku] = useState(() => {
    if (state.selectedMokuDropdownValue) return state.selectedMokuDropdownValue;
    if (state.selectedMoku && MOKU_OPTIONS.some(opt => opt.value === state.selectedMoku)) return state.selectedMoku;
    if (resumeData?.moku != null && resumeData?.island != null) {
      const opt = MOKU_OPTIONS.find(o => o.moku === resumeData.moku && o.island === resumeData.island);
      return opt ? opt.value : "general";
    }
    return "general";
  });
  const [fileTypes, setFileTypes] = useState(state.fileTypes ?? {});
  const [loading, setLoading] = useState(false);

  const { files, setFiles } = useContext(FileContext);
  const navigate = useNavigate();

  useEffect(() => {
    const standardsFile = selectedSubject === 'cs' ? 'full_standards.json' : 
                         selectedSubject === 'math' ? 'math_standards.json' : 
                         'science_standards.json';
    
    fetch(`/assets/${standardsFile}`)
      .then((res) => res.json())
      .then((data) => setStandardsData(data))
      .catch((err) => console.error(`Failed to load ${standardsFile}:`, err));
  }, [selectedSubject]);

  const getGradeLevels = () => standardsData?.grades || [];

  /** Grade dropdown: render from JSON grades (Math: K–8 + High School: <concept>; Science: K–5 + Middle School, High School). */
  const getGradeOptions = () => {
    if (!standardsData || !standardsData.grades) return [];
    return standardsData.grades.map((g) => ({ value: g, label: g }));
  };

  /**
   * Standards data adapter:
   * - Math: grades are K–8 or "High School: <concept>" (key in standards)
   * - Science: grades K–5, "Middle School", "High School" → lookup keys "K"-"5", "Middle School", "High School"
   * - CS: keyed by band level from gradeLevels
   */
  const getStandardsLevelKey = () => {
    if (!standardsData || !selectedGrade) return null;

    // Science: support legacy MS/HS from saved state
    if (selectedSubject === 'science') {
      if (selectedGrade === 'MS') return 'Middle School';
      if (selectedGrade === 'HS') return 'High School';
    }

    // Math/Science (numeric or "High School: X"): key exists in standards
    if (standardsData?.standards?.[selectedGrade]) return selectedGrade;

    // CS: keyed by band "level" from gradeLevels
    const gradeLevelEntry = Object.entries(standardsData.gradeLevels || {}).find(([, value]) =>
      value.grades?.includes(selectedGrade)
    );
    if (!gradeLevelEntry) return null;

    const [, gradeLevelValue] = gradeLevelEntry;
    if (gradeLevelValue.level && standardsData?.standards?.[gradeLevelValue.level]) {
      return gradeLevelValue.level;
    }
    return gradeLevelEntry[0];
  };

  const getConceptsNodeForGrade = () => {
    if (!standardsData || !selectedGrade) return null;
    const levelKey = getStandardsLevelKey();
    if (!levelKey) return null;
    const node = standardsData?.standards?.[levelKey];
    if (!node) return null;

    // Science per-grade wrapper: { grade: "...", standards: { Concept: [...] } }
    if (node.standards && typeof node.standards === "object" && !Array.isArray(node.standards)) {
      return node.standards;
    }

    // CS + Math: { Concept: { subconcepts: [...], standards: [...] } }
    return node;
  };

  const getConceptsForGrade = () => {
    const conceptsNode = getConceptsNodeForGrade();
    if (!conceptsNode) return [];
    return Object.keys(conceptsNode);
  };

  /** Concept dropdown: exact concept names (grade already encodes "High School: X" for Math HS). */
  const getConceptOptions = () => {
    const concepts = getConceptsForGrade();
    if (!concepts.length) return [];
    return concepts.map((c) => ({ value: c, label: c }));
  };

  const getSubconceptsForGradeAndConcept = () => {
    if (!selectedConcept) return [];
    const conceptsNode = getConceptsNodeForGrade();
    if (!conceptsNode) return [];

    const conceptNode = conceptsNode?.[selectedConcept];
    // Science: array of standards objects for this concept
    if (Array.isArray(conceptNode)) {
      const subconcepts = conceptNode
        .map((s) => s?.subconcept)
        .filter(Boolean);
      return Array.from(new Set(subconcepts)).sort((a, b) => a.localeCompare(b));
    }

    // CS + Math: { subconcepts: [...] }
    return conceptNode?.subconcepts || [];
  };

  const getStandardsForSubconcept = () => {
    if (!selectedConcept || !selectedSubconcept) return [];
    const conceptsNode = getConceptsNodeForGrade();
    if (!conceptsNode) return [];

    const conceptNode = conceptsNode?.[selectedConcept];
    // Science: conceptNode is an array of standard objects
    if (Array.isArray(conceptNode)) {
      return conceptNode.filter((s) => s?.subconcept === selectedSubconcept);
    }

    // CS + Math
    const allStandards = conceptNode?.standards || [];
    return allStandards.filter((s) => s?.subconcept === selectedSubconcept);
  };

  const mokuOptions = MOKU_OPTIONS;

  const handleNext = async (e) => {
    e.preventDefault();
    console.log("Selected Subject:", selectedSubject);
    console.log("Selected Grade:", selectedGrade);
    console.log("Selected Concept:", selectedConcept);
    console.log("Selected Subconcept:", selectedSubconcept);
    console.log("Selected Standard:", selectedStandard);
    console.log("Selected Language:", selectedLanguage);
    console.log("Is Resuming:", isResuming);
    console.log("Resume Data:", resumeData);
    
    if (!selectedSubject || !selectedGrade || !selectedConcept || !selectedSubconcept || !selectedStandard) {
      alert("Please select Subject, Grade, Concept, Subconcept, and Standard before proceeding.");
      return;
    }

    // Extract moku and island from selected moku option
    const selectedMokuOption = mokuOptions.find(opt => opt.value === selectedMoku);
    const selectedMokuName = selectedMokuOption ? selectedMokuOption.moku : selectedMoku;
    const selectedIsland = selectedMokuOption ? selectedMokuOption.island : "";

    // Reuse cached elements if user went back and submitted same inputs (save compute)
    const cached = state.generatedElements && typeof state.generatedElements === 'object';
    const sameInputs = cached &&
      state.selectedSubject === selectedSubject &&
      state.selectedGrade === selectedGrade &&
      state.selectedConcept === selectedConcept &&
      state.selectedSubconcept === selectedSubconcept &&
      state.selectedStandardIdentifier === selectedStandard?.identifier &&
      state.selectedLanguage === selectedLanguage &&
      state.selectedMoku === selectedMokuName;
    if (sameInputs) {
      const baseNav = {
        selectedSubject,
        selectedGrade,
        selectedConcept,
        selectedSubconcept,
        selectedStandardIdentifier: selectedStandard.identifier,
        selectedStandardDescription: selectedStandard.standard,
        selectedLanguage,
        selectedMoku: selectedMokuName,
        selectedMokuDropdownValue: selectedMoku,
        selectedIsland,
        files,
        fileTypes,
        generatedElements: state.generatedElements,
        selectedElements: state.selectedElements || { story: false, proverb: false, cultural_practice: false },
        language: selectedLanguage,
      };
      const navigationData = isResuming ? { ...baseNav, resumeData, isResuming: true } : baseNav;
      navigate("/select-elements", { state: navigationData });
      return;
    }
  
    setLoading(true);
  
    console.log("Submitting lesson plan with data:", {
      subject: selectedSubject,
      grade: selectedGrade,
      concept: selectedConcept,
      subconcept: selectedSubconcept,
      standard: selectedStandard.identifier,
      standard_description: selectedStandard.standard,
      language: selectedLanguage,
      moku: selectedMokuName,
      island: selectedIsland,
    });
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/generate_elements/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { 'Authorization': `Token ${token}` }),
        },
        credentials: 'include',
        body: JSON.stringify({
          subject: selectedSubject,
          grade: selectedGrade,
          concept: selectedConcept,
          subconcept: selectedSubconcept,
          standard: selectedStandard.identifier,
          standard_description: selectedStandard.standard,
          language: selectedLanguage,
          moku: selectedMokuName,
          island: selectedIsland,
        }),
      });
  
      if (!response.ok) {
        throw new Error("Failed to fetch generated elements.");
      }
  
      const generatedElements = await response.json();
      track('Create Lesson Plan - Elements Generated', { lesson_plan_id: generatedElements.id, screen_name: 'Create Lesson Plan' });
      // add timeout for 10 seconds   
    //   await new Promise((resolve) => setTimeout(resolve, 10000));
    //   const response = {
    //     "id": "cbbf7a92-fcb4-4ba3-b56b-ca006e8a9072",
    //     "story": {
    //         "mo_olelo_name": "Māui a me ka Hiki ʻana o ka Lā",
    //         "mo_olelo": "Ma nā moʻolelo Hawaiʻi kuʻuna, ʻo kekahi o nā hana kaulana loa a Māui, ʻo ia ka wā i hoʻopaʻa ai ʻo ia i ka lā e hoʻomaha loa ana i kona holoholo ʻana ma ka lani, e hoʻouka ana i ka pōkole loa o nā lā. Hoʻomaʻamaʻa ʻo Māui i ka hoʻopaʻa ʻana i nā aho ikaika a me ka ʻikepili mai kona makuahine e pili ana i ke ala hele o ka lā. Me kāna ʻike a me nā hoʻokolokolo ʻana i nā mea hana, ua kūleʻa ʻo Māui i ka hoʻopaʻa ʻana i ka lā a hoʻopaʻa i ka wā lōʻihi o nā lā, e kōkua ana i ke kaiāulu e noho pono a hana me ka maikaʻi.",
    //         "connection": "ʻO ka moʻolelo o Māui e hoʻopaʻa ana i ka lā e hōʻike ana i ka hana hoʻokolokolo ʻana i ka ʻike (nā aho ikaika, nā pōhaku, a me ka ʻike mai kona makuahine) ma mua o ka hana ʻana. Ma ke aʻo ʻana i ka 'CS standard: Systematically test and refine programs using a range of test cases,' hiki i nā haumāna ke hoʻomaopopo i ka pili o ka hoʻāʻo ʻana i nā mea like ʻole e hana ai i ka hopena kūpono loa, e like me ka hoʻokolokolo ʻana a Māui ma mua o kona kūleʻa ʻana.",
    //         "verification_note": "Verified: ʻO ka moʻolelo ʻO Māui a me ka Hiki ʻana o ka Lā he moʻolelo kaulana loa i hōʻoia ʻia ma nā kumuwaiwai e like me *Hawaiian Mythology* na Martha Beckwith a me nā hana ʻē aʻe e pili ana iā Māui. Hoʻomaopopo ka moʻolelo i ka ʻike o Māui i ka hoʻokolokolo a me ka hoʻomohala ʻana i kāna hana e kūleʻa ai."
    //     },
    //     "proverb": {
    //         "olelo_noeau": "He aliʻi ka ʻāina; he kauwā ke kanaka.",
    //         "translation": "The land is the chief; man is its servant.",
    //         "connection": "Ma ke aʻo ʻana i ka 'CS standard: Systematically test and refine programs using a range of test cases,' hiki i nā haumāna ke ʻike i ka pili o ka mālama ʻana i ka ʻāina me ka hoʻokō ʻana i nā hana kūpono. ʻO ka 'hoʻokolokolo' ʻana i nā mana hana e mālama pono ai i ka ʻāina, e like me ka manaʻo o kēia ʻōlelo noʻeau, he mea koʻikoʻi ia e hoʻokō ai i nā hopena maikaʻi.",
    //         "verification_note": "Verified: ʻO ka ʻōlelo noʻeau 'He aliʻi ka ʻāina; he kauwā ke kanaka' i ʻike ʻia ma *ʻŌlelo Noʻeau: Hawaiian Proverbs and Poetical Sayings* na Mary Kawena Pukui, he ʻōlelo noʻeau i hōʻoia ʻia no ka pili o ke kanaka i ka mālama ʻana i ka ʻāina."
    //     },
    //     "cultural_practice": {
    //         "cultural_practice_name": "Hoʻopae ʻia ka ʻupena lawaiʻa",
    //         "full_cultural_practice": "Ua ʻike ka poʻe Hawaiʻi kuʻuna i ke ʻano koʻikoʻi o ka hoʻopaʻa ʻana i nā ʻupena lawaiʻa i mea e lawaiʻa pono ai. Ma mua o ka lawe ʻana i nā ʻupena i ke kai, pono lākou e hōʻoia i ka ikaika o ka wili ʻana, ka paʻa ʻana o nā kaula, a me ka kūpono o ka lōʻihi o nā aho. Hoʻokolokolo nui ʻia kēia ma ka honua, ma nā ʻāina ʻāpana o ke kahakai, e hōʻoia i ka paʻa o ka ʻupena a me kona hiki ke loaʻa ka iʻa me ka maikaʻi.",
    //         "connection": "Ma ke aʻo ʻana i ka 'CS standard: Systematically test and refine programs using a range of test cases,' hiki i nā haumāna ke aʻo mai i ke akamai o ka hoʻokolokolo ʻana i nā ʻāpana o kahi papahana (e like me ka ʻupena) ma mua o ka hana piha ʻana. ʻO kēia kaʻina hana, e like me ka hana moʻomeheu o ka hoʻopaʻa ʻana i nā ʻupena lawaiʻa, e kōkua ana i ka hoʻokō ʻana i nā hopena maikaʻi loa i ka hoʻomohala ʻana o nā polokalamu a kākou.",
    //         "verification_note": "Verified: Ua hōʻoia ʻia ke ʻano o ka hana moʻomeheu o ka hoʻopae ʻana i ka ʻupena lawaiʻa ma nā kumuwaiwai e pili ana i ka ʻike Hawaiʻi kuʻuna, me nā ʻike mai ke Kulanui o Hawaiʻi a me ka Hale Hōʻikeʻike ʻo Pīhopa. ʻO ka hoʻokolokolo ʻana i ka paʻa o nā ʻupena he hana koʻikoʻi ma ka lawaiʻa kuʻuna."
    //     }
    // }
    //   const generatedElements = response;
    // const generatedElements = "";
      const baseNav = {
        selectedSubject,
        selectedGrade,
        selectedConcept,
        selectedSubconcept,
        selectedStandardIdentifier: selectedStandard.identifier,
        selectedStandardDescription: selectedStandard.standard,
        selectedLanguage,
        selectedMoku: selectedMokuName,
        selectedMokuDropdownValue: selectedMoku,
        selectedIsland,
        files,
        fileTypes,
        generatedElements,
        language: selectedLanguage,
      };
      const navigationData = isResuming ? { ...baseNav, resumeData, isResuming: true } : baseNav;
      navigate("/select-elements", { state: navigationData });
    } catch (error) {
      console.error("Error during lesson plan submission:", error);
      alert("Something went wrong while preparing your lesson plan. Please try again.");
    } finally {
      setLoading(false); // <-- Stop spinner
    }
  };
    
  return (
    <>
    {loading && (
      <div className={styles.loadingOverlay}>
        <div className={styles.loadingModal}>
          <p className={styles.loadingHeadline}>
            Verifying cultural authenticity…
          </p>
          <img
            src="/assets/KumuConnectLoading2.gif"
            alt="Loading..."
            className={styles.loaderImage}
          />
          <p className={styles.loadingBody}>
            Kumu Connect is carefully researching and cross-referencing multiple
            sources to ensure every story, proverb, and practice we share is
            accurate and authentic. This might take a moment, but it&apos;s worth
            the wait.
          </p>
        </div>
      </div>
    )}
    <form className={styles.form} onSubmit={handleNext}>
      <h1 className={styles.title}>{t('create_lesson_plan_title', uiLanguage)}</h1>
      <p className={styles.subtitle}>{t('create_lesson_plan_subtitle', uiLanguage)}</p>
      <div className={styles.formGrid}>

  <FormField
    label={t('subject', uiLanguage)}
    required
    type="dropdown"
    value={selectedSubject}
    onChange={(e) => {
      setSelectedSubject(e.target.value);
      setSelectedGrade("");
      setSelectedConcept("");
      setSelectedSubconcept("");
      setSelectedStandard("");
    }}
    options={[
      { value: "cs", label: t('computer_science', uiLanguage) },
      { value: "math", label: t('mathematics', uiLanguage) },
      { value: "science", label: t('science', uiLanguage) }
    ]}
  />

  <FormField
    label={t('grade', uiLanguage)}
    required
    type="dropdown"
    value={selectedGrade}
    onChange={(e) => {
      setSelectedGrade(e.target.value);
      setSelectedConcept("");
      setSelectedSubconcept("");
      setSelectedStandard("");
    }}
    disabled={!selectedSubject}
    options={[{ value: "", label: t('select_option', uiLanguage) }, ...getGradeOptions()]}
  />


  <FormField
    label={t('concept_label', uiLanguage)}
    required
    type="dropdown"
    value={selectedConcept}
    onChange={(e) => {
      setSelectedConcept(e.target.value);
      setSelectedSubconcept("");
      setSelectedStandard("");
    }}
    disabled={!selectedGrade}
    options={[{ value: "", label: t('select_option', uiLanguage) }, ...getConceptOptions()]}
  />

  <FormField
    label={t('subconcept', uiLanguage)}
    required
    type="dropdown"
    value={selectedSubconcept}
    onChange={(e) => {
      setSelectedSubconcept(e.target.value);
      setSelectedStandard("");
    }}
    disabled={!selectedConcept}
    options={[{ value: "", label: t('select_option', uiLanguage) }, ...getSubconceptsForGradeAndConcept().map((sc) => ({ value: sc, label: sc }))]}
  />

  <FormField
    label={t('standard_label', uiLanguage)}
    required
    type="dropdown"
    value={selectedStandard ? JSON.stringify(selectedStandard) : ""}
    onChange={(e) => {
      const value = e.target.value;
      setSelectedStandard(value ? JSON.parse(value) : "");
    }}
    disabled={!selectedSubconcept}
    options={[
      { value: "", label: t('select_option', uiLanguage) },
      ...getStandardsForSubconcept().map((s) => ({
        value: JSON.stringify({ identifier: s.identifier, standard: s.standard }),
        label: `${s.identifier} — ${s.standard}`,
      })),
    ]}
  />

  {/* Right column bottom */}
  <FormField
    label={t('language', uiLanguage)}
    required
    type="dropdown"
    value={selectedLanguage}
    onChange={(e) => setSelectedLanguage(e.target.value)}
    options={[
      { value: "en", label: t('english', uiLanguage) },
      { value: "haw", label: t('olelo_hawaii_hawaiian', uiLanguage) },
    ]}
  />

  <FormField
    label="Moku (Optional)"
    type="dropdown"
    value={selectedMoku}
    onChange={(e) => setSelectedMoku(e.target.value)}
    options={mokuOptions.map((moku) => ({
      value: moku.value,
      label: moku.label,
    }))}
  />
</div>

      <footer className={styles.formFooter}>
        <button type="submit" className={styles.nextBtn}>
          <span className={styles.nextBtnText}>next</span>
          <div className={styles.nextBtnIcon}>
            <div className={styles.nextChevron}></div>
          </div>
        </button>
      </footer>
    </form>
    </>
  );
}

export default LessonPlanForm;
